import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../shared/components/PageHeader';
import { useTenant } from '../../core/contexts/TenantContext';
import { useLineOfBiz } from '../../core/contexts/LineOfBizContext';
import { resources } from '../../core/services/resources';
import type { LineOfBiz, ReportTemplate } from '../../core/types/data-models';

/**
 * Recursive node in the LOB hierarchy tree. Each LOB also lists its own
 * report templates as leaves so the user can jump straight from the
 * dashboard into a specific template's detail view.
 */
function HierarchyNode({
  lob,
  depth,
  childrenByParent,
  templatesByLob,
  activeLineOfBizId,
  onPickLob,
  onPickTemplate,
}: {
  lob: LineOfBiz;
  depth: number;
  childrenByParent: Map<number | null, LineOfBiz[]>;
  templatesByLob: Map<number, ReportTemplate[]>;
  activeLineOfBizId: number | null;
  onPickLob: (id: number) => void;
  onPickTemplate: (id: number) => void;
}) {
  const childLobs = childrenByParent.get(lob.id) ?? [];
  const templates = templatesByLob.get(lob.id) ?? [];
  const isActive = activeLineOfBizId === lob.id;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          pl: depth * 2,
          py: 0.4,
          borderRadius: 1,
          cursor: 'pointer',
          backgroundColor: isActive ? 'action.selected' : 'transparent',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onClick={() => onPickLob(lob.id)}
      >
        <BusinessIcon
          fontSize="small"
          sx={{ color: isActive ? 'primary.main' : 'text.secondary' }}
        />
        <Typography
          variant="body2"
          fontWeight={isActive ? 700 : 500}
          sx={{ flexGrow: 1 }}
          noWrap
        >
          {lob.name}
        </Typography>
        {templates.length > 0 && (
          <Chip size="small" label={templates.length} sx={{ height: 18 }} />
        )}
      </Stack>

      {templates.map((t) => (
        <Stack
          key={t.id}
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            pl: (depth + 1) * 2 + 1,
            py: 0.3,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onPickTemplate(t.id);
          }}
        >
          <AssessmentIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
            {t.name}
          </Typography>
          <Tooltip title="Open template">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPickTemplate(t.id);
              }}
            >
              <ArrowForwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      ))}

      {childLobs.map((c) => (
        <HierarchyNode
          key={c.id}
          lob={c}
          depth={depth + 1}
          childrenByParent={childrenByParent}
          templatesByLob={templatesByLob}
          activeLineOfBizId={activeLineOfBizId}
          onPickLob={onPickLob}
          onPickTemplate={onPickTemplate}
        />
      ))}
    </Box>
  );
}

export function DashboardPage() {
  const { activeTenantName } = useTenant();
  const { activeLineOfBizId, setActiveLineOfBizId } = useLineOfBiz();
  const navigate = useNavigate();

  // Pull the full LOB and template lists so we can build a single tree
  // client-side. Page-size of 500 is well above realistic tenant sizes.
  const lobs = useQuery({
    queryKey: ['lines-of-business', 'select'],
    queryFn: () => resources.linesOfBusiness.list({ pageSize: 500, sort: 'name' }),
  });
  const reportTemplates = useQuery({
    queryKey: ['report-templates', 'select'],
    queryFn: () => resources.reportTemplates.list({ pageSize: 500, sort: 'name' }),
  });

  const lobList: LineOfBiz[] = lobs.data?.data ?? [];
  const templateList: ReportTemplate[] = reportTemplates.data?.data ?? [];

  // `parentId = null` → root LOBs. Used by the recursive node renderer.
  const childrenByParent = useMemo(() => {
    const map = new Map<number | null, LineOfBiz[]>();
    for (const lob of lobList) {
      const key = lob.parentId ?? null;
      const list = map.get(key) ?? [];
      list.push(lob);
      map.set(key, list);
    }
    return map;
  }, [lobList]);
  const rootLobs = childrenByParent.get(null) ?? [];

  // Group templates by LOB so each node renders its own template leaves.
  const templatesByLob = useMemo(() => {
    const map = new Map<number, ReportTemplate[]>();
    for (const t of templateList) {
      const list = map.get(t.lineOfBizId) ?? [];
      list.push(t);
      map.set(t.lineOfBizId, list);
    }
    return map;
  }, [templateList]);

  // template id → lineOfBizId, used to keep the LOB context in sync when
  // the user picks a template node so the sidebar's LOB scope matches
  // the page we land on.
  const templateLobById = useMemo(() => {
    const map = new Map<number, number>();
    for (const t of templateList) map.set(t.id, t.lineOfBizId);
    return map;
  }, [templateList]);

  const handlePickLob = (id: number) => setActiveLineOfBizId(id);
  const handlePickTemplate = (id: number) => {
    const lobId = templateLobById.get(id);
    if (lobId != null) setActiveLineOfBizId(lobId);
    navigate(`/report-templates/${id}/prompts`);
  };

  const isLoading = lobs.isLoading || reportTemplates.isLoading;

  return (
    <Box>
      <PageHeader
        title={activeTenantName ?? ''}
        description="Active customer overview"
      />

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <BusinessIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              Lines of business
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Click a line of business to make it active, or pick a report template to open it.
          </Typography>
          {isLoading && rootLobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : rootLobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No lines of business yet.
            </Typography>
          ) : (
            <Stack spacing={0.25}>
              {rootLobs.map((lob) => (
                <HierarchyNode
                  key={lob.id}
                  lob={lob}
                  depth={0}
                  childrenByParent={childrenByParent}
                  templatesByLob={templatesByLob}
                  activeLineOfBizId={activeLineOfBizId}
                  onPickLob={handlePickLob}
                  onPickTemplate={handlePickTemplate}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
